import { Dialog, Transition } from "@headlessui/react";
import {
  TrashIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { Fragment, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../../../components/CTAButtonComponents";
import { Select } from "../../../components/inputComponent";
import { useBulkCompanyAccess } from "../../../queries/companyStats/captableStats";
import { useGetAllHissaUsers } from "../../../queries/user";

type SelectedCompany = {
  id: string;
  name: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedCompanies: SelectedCompany[];
  onRemoveCompany: (companyId: string) => void;
  onAccessGranted: (companyIds: string[]) => void;
};

type SelectableUser = {
  email: string;
  fullName: string;
  source: "hissa" | "manual";
};

type HissaUserApiRecord = {
  user?: {
    emailId?: string | null;
    fullName?: string | null;
  };
};

const MAX_BULK_COMPANIES = 100;
const MAX_BULK_USERS = 100;
const USER_SUGGESTION_LIMIT = 60;

const designationOptions = [
  { value: "FOUNDER", label: "Founder" },
  { value: "CFO", label: "CFO" },
  { value: "DIRECTOR", label: "Director" },
  { value: "ATTORNEY", label: "Attorney" },
  { value: "COMPANY_SECRETARY", label: "Company Secretary" },
  { value: "CHARTED_ACCOUNTANT", label: "Chartered Accountant" },
  { value: "COMPLIANCE_OFFICER", label: "Compliance Officer" },
  { value: "CONSULTANT", label: "Consultant" },
  { value: "OTHERS", label: "Others" },
];

const normalizeValue = (value: string) => value.trim().toLowerCase();

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const getRoleGrants = (roleName: string) => {
  if (roleName === "Admin") {
    return [{ role: "Admin", action: "*", resource: "*", attributes: ["*"] }];
  }
  return [
    {
      role: "Admin Viewer",
      action: "read",
      resource: "*",
      attributes: ["*"],
    },
  ];
};

function getUserSearchScore(user: SelectableUser, query: string) {
  if (!query) return 1;
  const email = user.email;
  const fullName = normalizeValue(user.fullName || "");
  const [localPart = "", domainPart = ""] = email.split("@");
  const queryWithAt = query.startsWith("@") ? query : `@${query}`;

  let score = -1;

  if (domainPart === query) score = Math.max(score, 1300);
  if (domainPart.startsWith(query)) score = Math.max(score, 1200);
  if (`@${domainPart}`.startsWith(queryWithAt)) score = Math.max(score, 1150);
  if (domainPart.includes(query)) score = Math.max(score, 1100);

  if (email.startsWith(query)) score = Math.max(score, 1000);
  if (localPart.startsWith(query)) score = Math.max(score, 900);
  if (fullName.startsWith(query)) score = Math.max(score, 800);

  if (email.includes(query)) score = Math.max(score, 700);
  if (localPart.includes(query)) score = Math.max(score, 600);
  if (fullName.includes(query)) score = Math.max(score, 500);

  return score;
}

function BulkUserAccessPopup({
  isOpen,
  onClose,
  selectedCompanies,
  onRemoveCompany,
  onAccessGranted,
}: Props) {
  const { mutate: bulkAccess } = useBulkCompanyAccess();
  const {
    data: hissaUsersData,
    isLoading: isLoadingHissaUsers,
    isError: isHissaUsersError,
  } = useGetAllHissaUsers();

  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SelectableUser[]>([]);
  const [roleName, setRoleName] = useState("Admin");
  const [designation, setDesignation] = useState("OTHERS");
  const [silentAccess, setSilentAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedUserSearchTerm = normalizeValue(userSearchTerm);

  const uniqueSelectedCompanies = useMemo(() => {
    const map = new Map<string, SelectedCompany>();
    selectedCompanies.forEach((company) => {
      if (!map.has(company.id)) map.set(company.id, company);
    });
    return Array.from(map.values());
  }, [selectedCompanies]);

  const filteredCompanies = useMemo(() => {
    const query = companySearchTerm.trim().toLowerCase();
    if (!query) return uniqueSelectedCompanies;
    return uniqueSelectedCompanies.filter(
      (company) =>
        company.name.toLowerCase().includes(query) ||
        company.id.toLowerCase().includes(query),
    );
  }, [companySearchTerm, uniqueSelectedCompanies]);

  const allHissaUsers = useMemo(() => {
    const usersMap = new Map<string, SelectableUser>();
    (hissaUsersData as HissaUserApiRecord[] | undefined)?.forEach((record) => {
      const email = normalizeValue(record?.user?.emailId || "");
      if (!email || !isValidEmail(email)) return;
      if (usersMap.has(email)) return;
      usersMap.set(email, {
        email,
        fullName: record?.user?.fullName?.trim() || email.split("@")[0],
        source: "hissa",
      });
    });
    return Array.from(usersMap.values()).sort((a, b) =>
      a.email.localeCompare(b.email),
    );
  }, [hissaUsersData]);

  const selectedUserEmailSet = useMemo(
    () => new Set(selectedUsers.map((user) => user.email)),
    [selectedUsers],
  );

  const hissaUserEmailSet = useMemo(
    () => new Set(allHissaUsers.map((user) => user.email)),
    [allHissaUsers],
  );

  const rankedUserSuggestions = useMemo(() => {
    if (!normalizedUserSearchTerm) {
      return allHissaUsers;
    }
    return allHissaUsers
      .map((user) => ({
        user,
        score: getUserSearchScore(user, normalizedUserSearchTerm),
      }))
      .filter((entry) => entry.score >= 0)
      .sort(
        (a, b) => b.score - a.score || a.user.email.localeCompare(b.user.email),
      )
      .map((entry) => entry.user);
  }, [allHissaUsers, normalizedUserSearchTerm]);

  const visibleUserSuggestions = useMemo(
    () => rankedUserSuggestions.slice(0, USER_SUGGESTION_LIMIT),
    [rankedUserSuggestions],
  );

  const filteredUnselectedUsers = useMemo(
    () =>
      rankedUserSuggestions.filter(
        (user) => !selectedUserEmailSet.has(user.email),
      ),
    [rankedUserSuggestions, selectedUserEmailSet],
  );

  const canAddCustomUser = useMemo(() => {
    if (!normalizedUserSearchTerm) return false;
    if (!isValidEmail(normalizedUserSearchTerm)) return false;
    if (selectedUserEmailSet.has(normalizedUserSearchTerm)) return false;
    return !hissaUserEmailSet.has(normalizedUserSearchTerm);
  }, [hissaUserEmailSet, normalizedUserSearchTerm, selectedUserEmailSet]);

  const invalidEmails = useMemo(
    () =>
      selectedUsers
        .map((user) => user.email)
        .filter((email) => !isValidEmail(email)),
    [selectedUsers],
  );

  const addSelectedUser = (user: SelectableUser) => {
    if (selectedUsers.length >= MAX_BULK_USERS) {
      toast(`You can add a maximum of ${MAX_BULK_USERS} users at a time`, {
        type: "error",
        autoClose: 2500,
      });
      return;
    }
    if (selectedUserEmailSet.has(user.email)) return;
    setSelectedUsers((prev) => [...prev, user]);
  };

  const addCustomUserFromSearch = () => {
    if (!normalizedUserSearchTerm) return;
    if (!isValidEmail(normalizedUserSearchTerm)) {
      toast("Enter a valid email id to add a new user", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    addSelectedUser({
      email: normalizedUserSearchTerm,
      fullName: normalizedUserSearchTerm.split("@")[0],
      source: "manual",
    });
  };

  const removeSelectedUser = (email: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.email !== email));
  };

  const toggleUserSelection = (user: SelectableUser) => {
    if (selectedUserEmailSet.has(user.email)) {
      removeSelectedUser(user.email);
      return;
    }
    addSelectedUser(user);
  };

  const selectAllFilteredUsers = () => {
    if (filteredUnselectedUsers.length === 0) return;
    const remainingSlots = MAX_BULK_USERS - selectedUsers.length;
    if (remainingSlots <= 0) {
      toast(`You can add a maximum of ${MAX_BULK_USERS} users`, {
        type: "error",
        autoClose: 2500,
      });
      return;
    }
    const usersToAdd = filteredUnselectedUsers.slice(0, remainingSlots);
    setSelectedUsers((prev) => [...prev, ...usersToAdd]);
    if (filteredUnselectedUsers.length > usersToAdd.length) {
      toast(
        `Only ${usersToAdd.length} users were added due to the ${MAX_BULK_USERS} user limit`,
        {
          type: "warning",
          autoClose: 2500,
        },
      );
    }
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    const selectedCompanyIds = Array.from(
      new Set(uniqueSelectedCompanies.map((company) => company.id)),
    );
    const selectedEmails = selectedUsers.map((user) => user.email);

    if (selectedCompanyIds.length === 0 || selectedEmails.length === 0) {
      toast("Select at least one company and one user", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (selectedCompanyIds.length > MAX_BULK_COMPANIES) {
      toast(`You can select a maximum of ${MAX_BULK_COMPANIES} companies`, {
        type: "error",
        autoClose: 2500,
      });
      return;
    }

    if (selectedEmails.length > MAX_BULK_USERS) {
      toast(`You can add a maximum of ${MAX_BULK_USERS} users`, {
        type: "error",
        autoClose: 2500,
      });
      return;
    }

    if (invalidEmails.length > 0) {
      toast("Fix invalid email ids before submitting", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    const fullNameByEmail = selectedUsers.reduce(
      (acc, user) => ({
        ...acc,
        [user.email]: user.fullName,
      }),
      {} as Record<string, string>,
    );

    setIsSubmitting(true);
    bulkAccess(
      {
        companyIds: selectedCompanyIds,
        emails: selectedEmails,
        fullNameByEmail,
        roleName,
        designation,
        roleGrants: getRoleGrants(roleName),
        silentAccess,
      },
      {
        onSuccess: () => {
          toast(
            silentAccess
              ? "Bulk access granted successfully"
              : "Bulk access granted and notification emails sent",
            {
              type: "success",
              autoClose: 2000,
            },
          );
          onClose();
          onAccessGranted(selectedCompanyIds);
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.errorMessage || "Failed to grant bulk access",
            {
              type: "error",
              autoClose: 2000,
            },
          );
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  onClick={onClose}
                  aria-label="Close bulk access popup"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <Dialog.Title
                  as="h3"
                  className="text-lg font-semibold leading-6 text-gray-900"
                >
                  Bulk Access
                </Dialog.Title>
                <p className="text-sm text-gray-600 mt-2">
                  {uniqueSelectedCompanies.length} selected{" "}
                  {uniqueSelectedCompanies.length === 1
                    ? "company"
                    : "companies"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Max {MAX_BULK_COMPANIES} companies and {MAX_BULK_USERS} users
                  per bulk access request.
                </p>

                <div className="mt-3 rounded border border-gray-200">
                  <div className="border-b border-gray-200 p-2">
                    <label
                      htmlFor="company-search"
                      className="block text-sm text-gray-700"
                    >
                      Search Selected Companies
                    </label>
                    <input
                      id="company-search"
                      type="text"
                      value={companySearchTerm}
                      onChange={(event) =>
                        setCompanySearchTerm(event.target.value)
                      }
                      placeholder="Search by company name or company id"
                      className="mt-1 block w-full rounded-md border-0 text-grey-900 placeholder:text-gray-400 ring-gray-300 ring-1 ring-inset"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">#</th>
                          <th className="px-3 py-2 text-left font-medium">
                            Company Name
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            Company ID
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCompanies.map((company, index) => (
                          <tr
                            key={company.id}
                            className="border-t border-gray-100"
                          >
                            <td className="px-3 py-2 text-gray-600">
                              {index + 1}
                            </td>
                            <td className="px-3 py-2 text-gray-900">
                              {company.name}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {company.id}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                className="inline-flex items-center text-red-600 hover:text-red-800"
                                onClick={() => onRemoveCompany(company.id)}
                                title="Remove company from selection"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredCompanies.length === 0 && (
                          <tr>
                            <td
                              className="px-3 py-3 text-center text-gray-500"
                              colSpan={4}
                            >
                              No companies match your search
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="user-search-input" className="block text-sm">
                    Users ({selectedUsers.length}/{MAX_BULK_USERS})
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="user-search-input"
                      type="text"
                      value={userSearchTerm}
                      onChange={(event) =>
                        setUserSearchTerm(event.target.value)
                      }
                      onFocus={() => setIsUserDropdownOpen(true)}
                      onBlur={() =>
                        setTimeout(() => setIsUserDropdownOpen(false), 150)
                      }
                      onKeyDown={(event) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        if (canAddCustomUser) {
                          addCustomUserFromSearch();
                          return;
                        }
                        if (visibleUserSuggestions.length > 0) {
                          addSelectedUser(visibleUserSuggestions[0]);
                        }
                      }}
                      placeholder="Search users by email/name. Domain matches are prioritized."
                      className="block w-full rounded-md border-0 text-grey-900 placeholder:text-gray-400 ring-gray-300 ring-1 ring-inset"
                    />
                    {isUserDropdownOpen && (
                      <div className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-3 py-2">
                          <p className="text-xs text-gray-500">
                            {rankedUserSuggestions.length} filtered
                          </p>
                          <button
                            type="button"
                            className="text-xs font-medium text-primary disabled:cursor-not-allowed disabled:text-gray-400"
                            disabled={filteredUnselectedUsers.length === 0}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={selectAllFilteredUsers}
                          >
                            Select all filtered
                          </button>
                        </div>
                        {isLoadingHissaUsers && (
                          <p className="px-3 py-2 text-sm text-gray-500">
                            Loading users...
                          </p>
                        )}
                        {!isLoadingHissaUsers && isHissaUsersError && (
                          <p className="px-3 py-2 text-sm text-red-500">
                            Could not load Hissa users. You can still add a new
                            email manually.
                          </p>
                        )}
                        {!isLoadingHissaUsers &&
                          !isHissaUsersError &&
                          visibleUserSuggestions.map((user) => (
                            <button
                              key={user.email}
                              type="button"
                              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                                selectedUserEmailSet.has(user.email)
                                  ? "bg-orange-50"
                                  : ""
                              }`}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => toggleUserSelection(user)}
                            >
                              <span className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={selectedUserEmailSet.has(user.email)}
                                />
                                <span className="font-medium text-gray-900">
                                  {user.email}
                                </span>
                              </span>
                              <span className="ml-2 text-xs text-gray-500 truncate">
                                {user.fullName}
                              </span>
                            </button>
                          ))}
                        {!isLoadingHissaUsers &&
                          !isHissaUsersError &&
                          visibleUserSuggestions.length === 0 &&
                          !canAddCustomUser && (
                            <p className="px-3 py-2 text-sm text-gray-500">
                              No users found for this search.
                            </p>
                          )}
                        {canAddCustomUser && (
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm text-primary hover:bg-gray-50"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={addCustomUserFromSearch}
                          >
                            <UserPlusIcon className="h-4 w-4" />
                            Add new user: {normalizedUserSearchTerm}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <SecondaryCTAButton
                      onClick={() => setSelectedUsers([])}
                      disabled={selectedUsers.length === 0}
                    >
                      Clear Users
                    </SecondaryCTAButton>
                  </div>
                </div>

                <div className="mt-3 max-h-28 overflow-y-auto rounded border border-gray-200 p-2">
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map((user) => (
                      <span
                        key={user.email}
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                          isValidEmail(user.email)
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {user.email}
                        {user.source === "manual" && (
                          <span className="rounded bg-white px-1 text-[10px] font-semibold text-gray-600">
                            NEW
                          </span>
                        )}
                        <button
                          type="button"
                          className="inline-flex items-center"
                          onClick={() => removeSelectedUser(user.email)}
                          title="Remove user"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {selectedUsers.length === 0 && (
                      <p className="text-sm text-gray-500">
                        No users selected yet
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="roleName" className="block text-sm">
                      Role Name
                    </label>
                    <Select
                      onChange={(event) => setRoleName(event.target.value)}
                      value={roleName}
                      options={["Admin", "Admin Viewer"]}
                    />
                  </div>
                  <div>
                    <label htmlFor="designation" className="block text-sm">
                      Designation
                    </label>
                    <Select
                      onChange={(event) => setDesignation(event.target.value)}
                      value={designation}
                      options={designationOptions}
                      valueGetter={(option) => option.value}
                      textGetter={(option) => option.label}
                    />
                  </div>
                </div>

                <fieldset className="mt-4">
                  <legend className="text-sm font-medium text-gray-900">
                    Access Mode
                  </legend>
                  <div className="mt-2 space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        checked={!silentAccess}
                        onChange={() => setSilentAccess(false)}
                      />
                      Send notification emails
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        checked={silentAccess}
                        onChange={() => setSilentAccess(true)}
                      />
                      Silent access (no email)
                    </label>
                  </div>
                </fieldset>

                {invalidEmails.length > 0 && (
                  <p className="mt-3 text-sm text-red-600">
                    Invalid emails: {invalidEmails.join(", ")}
                  </p>
                )}

                <div className="mt-5 flex justify-end gap-3">
                  <SecondaryCTAButton onClick={onClose}>
                    Cancel
                  </SecondaryCTAButton>
                  <PrimaryCTAButton
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Applying..." : "Apply Bulk Access"}
                  </PrimaryCTAButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default BulkUserAccessPopup;
